import React from 'react';
import { StatisticsChart } from './StatisticsChart';

interface TopUser {
  user_id: number;
  user_name: string;
  user_surname: string;
  management: string;
  office: string;
  order_count: number;
  total_spent: number;
  average_order_amount: number;
}

interface UserStatsByOffice {
  office: string;
  management: string;
  user_count: number;
  total_orders: number;
  total_spent: number;
  average_orders_per_user: number;
  average_spent_per_user: number;
}

interface UserDepartmentOfficeDetailSectionProps {
  topUsersByOrders: TopUser[];
  userStatsByOffice: UserStatsByOffice[];
}

const UserDepartmentOfficeDetailSection: React.FC<UserDepartmentOfficeDetailSectionProps> = ({ 
  topUsersByOrders,
  userStatsByOffice
}) => {
  interface DeptStats {
    management: string;
    total_spent: number;
  }

  // Group by department and calculate total spent
  const deptMap = new Map<string, DeptStats>();
  
  topUsersByOrders.forEach(row => {
    if (!deptMap.has(row.management)) {
      deptMap.set(row.management, {
        management: row.management,
        total_spent: row.total_spent || 0,
      });
    } else {
      const dept = deptMap.get(row.management)!;
      dept.total_spent += row.total_spent || 0;
    }
  });
  
  const stats: DeptStats[] = Array.from(deptMap.values());

  // Büro bazlı harcama datası
  const officeStats = userStatsByOffice?.map(office => ({
    office: office.office,
    total_spent: office.total_spent || 0,
  })) || [];

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col gap-8 h-auto">
      <div>
        <h3 className="font-bold text-lg mb-4 text-gray-800">
          Departmanlara Göre Toplam Harcama
        </h3>
        <div className="flex-1 flex items-center justify-center min-h-0 w-full overflow-hidden">
          <StatisticsChart
            type="bar"
            data={stats}
            dataKey="total_spent"
            xKey="management"
            title=""
            color="#6366f1"
            height={220}
          />
        </div>
      </div>
      <div>
        <h3 className="font-bold text-lg mb-4 text-gray-800">
          Bürolara Göre Toplam Harcama
        </h3>
        <div className="flex-1 flex items-center justify-center min-h-0 w-full overflow-hidden">
          <StatisticsChart
            type="bar"
            data={officeStats}
            dataKey="total_spent"
            xKey="office"
            title=""
            color="#10b981"
            height={220}
          />
        </div>
      </div>
      
      {/* Detaylı Departman-Büro Tablosu */}
      <div>
        <h3 className="font-bold text-lg mb-4 text-gray-800">
          Departman-Büro Detaylı İstatistikleri
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Departman
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Büro
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Kullanıcı Sayısı
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Sipariş Sayısı
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Toplam Harcama
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Ortalama Sipariş
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Ortalama Harcama
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {userStatsByOffice?.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.management}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {item.office}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {item.user_count}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {item.total_orders}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {item.total_spent?.toLocaleString('tr-TR', { 
                      style: 'currency', 
                      currency: 'TRY' 
                    })}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {item.average_orders_per_user?.toFixed(1)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {item.average_spent_per_user?.toLocaleString('tr-TR', { 
                      style: 'currency', 
                      currency: 'TRY' 
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserDepartmentOfficeDetailSection; 